/**
 * Shared atoms for the auth flow — split out of AuthHub.jsx in 2026-02.
 *
 * Every panel (LoginPanel, ConsentPanel, LawyerSignupPanel, etc.) imports
 * these atoms so the visual language stays consistent and the panels
 * themselves stay under the 200-LOC React-component budget.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Eye, EyeSlash, LockKey, ArrowRight,
} from "@phosphor-icons/react";
import LexiqWordmark from "../../components/LexiqWordmark";

/* Parse a bar-card date string (DD/MM/YYYY, D/M/YYYY, or YYYY-MM-DD) into a
 * UTC-midnight Date, or `null` if unparseable. Mirrors the backend helper
 * in `services/bar_card_validity.py::parse_card_date` so client-side and
 * server-side expiry checks agree byte-for-byte. */
export function parseCardDate(raw) {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim().replace(/[\.-]/g, "/");
  const parts = s.split("/").filter(Boolean);
  if (parts.length !== 3) return null;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => Number.isNaN(n))) return null;
  const [a, b, c] = nums;
  let y, m, d;
  if (a > 1900) { y = a; m = b; d = c; }        // YYYY/MM/DD
  else if (c > 1900) { d = a; m = b; y = c; }   // DD/MM/YYYY
  else return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

export const GLASS = "bg-white/55 backdrop-blur-md rounded-[28px] border border-white/60 shadow-[0_2px_18px_rgba(10,25,47,0.08)]";

/* Match body bg + iOS status-bar (theme-color) to the gradient TOP color so
   the device dead-zone (notch / status bar) blends with the page top.
   Also lock body scroll to prevent the auth page from scrolling up/down. */
export function useAuthShellBackground() {
  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.backgroundColor = "#E4E4E4";
    document.documentElement.style.backgroundColor = "#E4E4E4";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "#E4E4E4");
    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.documentElement.style.backgroundColor = prevHtmlBg;
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, []);
}

export function ScreenShell({ children }) {
  useAuthShellBackground();
  return (
    <div
      className="w-full font-tajawal"
      dir="rtl"
      data-testid="auth-hub"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          "linear-gradient(180deg,#E4E4E4 0%, #E4E4E4 calc(env(safe-area-inset-top, 0px) + 60px), #E0E0E0 40%, #D0D0D0 75%, #C7C7C7 100%)",
      }}
    >
      <div
        className="w-full h-full overflow-y-auto"
        style={{
          WebkitOverflowScrolling: "touch",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 24px)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        }}
      >
        <div className="px-4 sm:px-6">
          <Link to="/" className="flex flex-col items-center mb-5">
            <LexiqWordmark className="text-[44px]" testid="auth-lexiq" />
            <span className="text-[10.5px] tracking-[0.32em] text-[#5A6470] uppercase mt-0.5">
              Justice + AI
            </span>
          </Link>
          <div className="w-full max-w-md mx-auto">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function GoldButton({ children, type = "button", disabled, onClick, testid }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="w-full relative overflow-hidden inline-flex items-center justify-center gap-2 py-3.5 rounded-[22px]
                 bg-gradient-to-br from-[#E6C463] to-[#B8860B] text-[#0A192F] font-extrabold
                 text-[15.5px] shadow-[0_8px_22px_rgba(212,175,55,0.45)]
                 active:scale-[0.985] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

export function GhostBackButton({ onClick, testid = "auth-back-btn" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#3F4750]
                 px-3 py-1.5 rounded-full bg-white/65 hover:bg-white border border-white/70
                 active:scale-95 transition mb-3"
    >
      <ArrowRight size={16} weight="bold" />
      رجوع
    </button>
  );
}

export function FieldLabel({ children }) {
  return <label className="block text-[13px] font-bold text-[#0A192F] mb-1.5">{children}</label>;
}

/* Display-only field — looks like an input but is a div, so tapping it does
   NOT focus, does NOT open the mobile keyboard, and does NOT trigger the
   browser's scroll-into-view that pushes the page up. Used for fields whose
   value is auto-populated (e.g. name from bar-card scan). */
export function DisplayField({ icon: Icon, value, placeholder, ltr = false, testid }) {
  const shown = value && String(value).trim();
  return (
    <div className="relative">
      {Icon && (
        <Icon
          size={18}
          weight="duotone"
          className={`absolute top-1/2 -translate-y-1/2 ${ltr ? "left-3" : "right-3"} text-[#5A6470] pointer-events-none`}
        />
      )}
      <div
        data-testid={testid}
        dir={ltr ? "ltr" : "rtl"}
        className={[
          "w-full py-3 rounded-2xl bg-white/70 border border-white/70",
          "text-[15px] font-bold outline-none min-h-[46px]",
          ltr ? "pl-9 pr-3 text-left" : "pr-9 pl-3 text-right",
          shown ? "text-[#0A192F]" : "text-[#8A8A8A]/80",
        ].join(" ")}
      >
        {shown || placeholder}
      </div>
    </div>
  );
}

export function TextInput({ icon: Icon, ltr = false, ...props }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon
          size={18}
          weight="duotone"
          className={`absolute top-1/2 -translate-y-1/2 ${ltr ? "left-3" : "right-3"} text-[#5A6470] pointer-events-none`}
        />
      )}
      <input
        {...props}
        dir={ltr ? "ltr" : "rtl"}
        className={[
          "w-full py-3 rounded-2xl bg-white/70 border border-white/70",
          "text-[15px] font-bold text-[#0A192F] placeholder-[#8A8A8A]/80 outline-none",
          "transition-all focus:bg-white focus:border-[#D4AF37] focus:shadow-[0_2px_18px_rgba(10,25,47,0.08)]",
          ltr ? "pl-9 pr-3 text-left" : "pr-9 pl-3 text-right",
        ].join(" ")}
      />
    </div>
  );
}

export function PasswordInput({ show, onToggle, ...props }) {
  return (
    <div className="relative">
      <LockKey size={18} weight="duotone" className="absolute top-1/2 -translate-y-1/2 left-3 text-[#5A6470] pointer-events-none" />
      <input
        {...props}
        type={show ? "text" : "password"}
        dir="ltr"
        className="w-full pl-9 pr-10 py-3 rounded-2xl bg-white/70 border border-white/70
                   text-[15px] font-bold text-[#0A192F] placeholder-[#8A8A8A]/80 outline-none
                   focus:bg-white focus:border-[#D4AF37] focus:shadow-[0_2px_18px_rgba(10,25,47,0.08)] transition-all"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#5A6470] hover:text-[#0A192F]"
        tabIndex={-1}
        aria-label="إظهار/إخفاء"
      >
        {show ? <EyeSlash size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}
